import { FC, ReactNode } from 'react';
import { parseText } from '../../../utils/string/parseText';
import { LocaleData } from '../../../models/locale/locale-data';
import { ResolvedVariantCopy } from '../../../utils/landing/getVariantCopy';
import { ClockIcon } from '../../../assets/icons/ClockIcon';
import heroImage from '../../../assets/images/hero.jpg';

/**
 * Sections for the new-UX (`-v1`) landing designs — the approved Figma
 * "Finals" layout (RI-3982) with the 26-Aug-2026 product feedback applied.
 * The live designs keep using sections/index.tsx untouched; keeping the two
 * section sets separate means the A/B arms cannot drift into each other.
 *
 * Bespoke type sizes live in the `.v2-*` classes in assets/styles.css (plain
 * author CSS — arbitrary Tailwind values are dropped from dev-mode builds).
 */

export const RegisterNowBar: FC<{ text: string }> = ({ text }) => (
  <article className="v2-register-bar">
    <p>{text}</p>
  </article>
);

export const HeroImageStrip: FC = () => (
  <div className="v2-hero-img">
    <img src={heroImage} alt="" />
  </div>
);

/** The hero lead line — the page's sole h1 (headline removed per 28-Aug product
 * feedback; the register-within-60-days line carries the page's message). */
export const V2Hero: FC<{ subCopy: string }> = ({ subCopy }) => (
  <h1 className="v2-subcopy">{subCopy}</h1>
);

export const BenefitTicks: FC<{ ticks: string[] }> = ({ ticks }) => (
  <ul className="v2-benefits">
    {ticks.map((tick, index) => (
      <li key={index}>
        <span className="v2-tick">✓</span>
        {tick}
      </li>
    ))}
  </ul>
);

/** One CTA column: heading + button (+ optional sub-line), slotted by variant. */
export const V2Cta: FC<{ heading: ReactNode; children: ReactNode; subCopy?: string }> = ({
  heading,
  children,
  subCopy,
}) => (
  <div>
    <h3>{heading}</h3>
    {children}
    {subCopy ? <p className="v2-phonesub">{subCopy}</p> : null}
  </div>
);

export const SixtyDayBox: FC<{ header?: string; body?: string }> = ({ header, body }) =>
  header ? (
    <div className="v2-box">
      <div className="v2-box-title">
        <ClockIcon />
        {header}
      </div>
      <div className="v2-box-body">{body}</div>
    </div>
  ) : null;

/**
 * The shared page frame for all three new-UX variants — they differ only in
 * the CTA block slotted into the middle.
 */
export const V2Frame: FC<{
  data: LocaleData;
  copy: ResolvedVariantCopy;
  cta: ReactNode;
  singleCta?: boolean;
}> = ({ data, copy, cta, singleCta }) => (
  <div className="grid grid-cols-12">
    <div className="col-span-12">
      <RegisterNowBar text={copy.banner} />
      <HeroImageStrip />
      <div className="v2-main">
        <V2Hero subCopy={copy.heroSubCopy1} />
        <BenefitTicks ticks={copy.benefitTicks ?? data.benefits.slice(0, 3)} />
        <hr className="v2-divider" />
        <div className={singleCta ? 'v2-cta v2-cta-single' : 'v2-cta'}>{cta}</div>
        <SixtyDayBox header={copy.sixtyDayHeader} body={copy.sixtyDayBody} />
      </div>
    </div>
    <V2SmallPrint data={data} copy={copy} />
  </div>
);

/**
 * The legal footnote paragraphs. Same content slots as the live design's
 * SmallPrintSection, but the ** paragraph comes from the RESOLVED variant copy
 * so link-only-v1 can swap in its registration-flavoured wording.
 */
export const V2SmallPrint: FC<{ data: LocaleData; copy: ResolvedVariantCopy }> = ({
  data,
  copy,
}) => (
  <article className="col-span-12 flex flex-col gap-space-500 text-center border-t border-solid border-neutral-200">
    <div className="w-11/12 md:max-w-[75%] mx-auto grid grid-cols-12 *:col-span-12 my-space-700">
      <p className="body-text-small mb-space-200">
        {parseText(data?.heroAsterix?.[0] ?? '', data?.heroAsterixLinks ?? [])}
      </p>
      <p className="body-text-small mb-space-200">
        {parseText(copy.phoneNumberAsterix?.[0] ?? '', copy.phoneNumberAsterixLinks ?? [])}
      </p>
    </div>
  </article>
);
